import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import {
  KuukaudenJarjestysnumero,
  KuukaudenLahetysTyyppi,
  KuukausittainenLahetysPaiva,
  LaskuLahetyksenToistoasetukset,
  LaskuLahetyksenToistoasetusVirhe,
  LaskuToistoTyyppi,
  ToistonPaattymisTyyppi,
  Viikonpaiva,
  VuosittainenLahetysPaiva,
} from '../../model/lasku-toistuva'

export class LaskuLahetyksenToistoService {
  constructor(private _dateService: DateService) {}

  annaSeuraavaLahetysPvm(asetukset: LaskuLahetyksenToistoasetukset, edellinenLahetyspvm?: NumberDate): NumberDate {
    const lahtopaiva = edellinenLahetyspvm || asetukset.ensimmainenLahetyspvm
    const pvm = this._dateService.numberToDate(lahtopaiva)

    let seuraava: Date = null

    switch (asetukset.toistoTyyppi) {
      case LaskuToistoTyyppi.KERRAN:
        return edellinenLahetyspvm ? null : asetukset.ensimmainenLahetyspvm

      case LaskuToistoTyyppi.KUUKAUSI:
      case LaskuToistoTyyppi.KUUKAUSI_3:
      case LaskuToistoTyyppi.KUUKAUSI_6:
        const kuukausiVali = this.annaKuukausiVali(asetukset.toistoTyyppi)
        seuraava = this.laskeSeuraavaKuukausi(pvm, asetukset.lahetyspaiva as KuukausittainenLahetysPaiva, kuukausiVali)
        break

      case LaskuToistoTyyppi.VUOSI:
        seuraava = this.laskeSeuraavaVuosi(pvm, asetukset.lahetyspaiva as VuosittainenLahetysPaiva)
        break
    }

    return this._dateService.dateToNumber(seuraava)
  }

  annaKuukausiVali(tyyppi: LaskuToistoTyyppi): number {
    switch (tyyppi) {
      case LaskuToistoTyyppi.KUUKAUSI:
        return 1
      case LaskuToistoTyyppi.KUUKAUSI_3:
        return 3
      case LaskuToistoTyyppi.KUUKAUSI_6:
        return 6
      default:
        return 1
    }
  }

  laskeSeuraavaKuukausi(edellinenPvm: Date, lahetyspaiva: KuukausittainenLahetysPaiva, kuukausiVali: number): Date {
    const seuraava = this._dateService.lisaaKuukausia(edellinenPvm, kuukausiVali)

    if (lahetyspaiva.tyyppi === KuukaudenLahetysTyyppi.PAIVAMAARA) {
      return this.asetaPaivamaaraKuukauteen(seuraava, lahetyspaiva.paiva)
    } else {
      return this.asetaViikonpaivaKuukauteen(seuraava, lahetyspaiva.viikonpaiva, lahetyspaiva.jarjestysnumero)
    }
  }

  asetaPaivamaaraKuukauteen(pvm: Date, tavoitePaiva: number): Date {
    const kuukaudenViimeinenPaiva = this._dateService.paiviaKuukaudessa(pvm)
    const kuukaudenPaiva = Math.min(tavoitePaiva, kuukaudenViimeinenPaiva)
    return new Date(pvm.getFullYear(), pvm.getMonth(), kuukaudenPaiva)
  }

  asetaViikonpaivaKuukauteen(pvm: Date, viikonpaiva: Viikonpaiva, jarjestysnumero: KuukaudenJarjestysnumero): Date {
    if (jarjestysnumero === KuukaudenJarjestysnumero.VIIMEINEN) {
      return this.etsiKuukaudenViimeinenViikonpaiva(pvm, viikonpaiva)
    } else {
      return this.etsiNsViikonpaiva(pvm, viikonpaiva, jarjestysnumero)
    }
  }

  etsiKuukaudenViimeinenViikonpaiva(pvm: Date, viikonpaiva: Viikonpaiva): Date {
    const paivat = this._annaKuukaudenPaivat(pvm).reverse()

    for (const paiva of paivat) {
      const pv = paiva.getDay() || 7
      if (pv === viikonpaiva) {
        return new Date(paiva)
      }
    }
    return new Date(pvm)
  }

  etsiNsViikonpaiva(pvm: Date, viikonpaiva: Viikonpaiva, jarjestysnumero: KuukaudenJarjestysnumero): Date {
    const paivat = this._annaKuukaudenPaivat(pvm)
    let laskuri = 0

    for (const paiva of paivat) {
      const pv = paiva.getDay() || 7
      if (pv === viikonpaiva) {
        laskuri++
        if (laskuri === jarjestysnumero) {
          return new Date(paiva)
        }
      }
    }
    return new Date(pvm)
  }

  laskeSeuraavaVuosi(edellinenPvm: Date, lahetyspaiva: VuosittainenLahetysPaiva): Date {
    const vuosi = edellinenPvm.getFullYear() + 1
    const kuukausi = lahetyspaiva.kuukausi
    const tavoitePaiva = lahetyspaiva.paiva

    const ehdokasPvm = new Date(vuosi, kuukausi - 1, 1)
    const kuukaudenViimeinenPaiva = this._dateService.paiviaKuukaudessa(ehdokasPvm)
    const rajoitettuPaiva = Math.min(tavoitePaiva, kuukaudenViimeinenPaiva)

    return new Date(vuosi, kuukausi - 1, rajoitettuPaiva)
  }

  annaViimeinenLaskupvmJosLaskusarjassa(toisto: LaskuLahetyksenToistoasetukset, lahetyksia: number): Date | null {
    const paattyminen = toisto.paattyminen

    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.EI_PAATTYMISTA || toisto.toistoTyyppi === LaskuToistoTyyppi.KERRAN) {
      return null
    }
    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.PAATTYMISPVM) {
      return this._dateService.numberToDate(paattyminen.paattymispvm)
    }
    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.LAHETYSMAARA) {
      let pvm = toisto.ensimmainenLahetyspvm
      let lahetyksiaLaskettu = 1

      while (lahetyksiaLaskettu < paattyminen.lahetysmaara) {
        pvm = this.annaSeuraavaLahetysPvm(toisto, pvm)
        lahetyksiaLaskettu++
      }
      return this._dateService.numberToDate(pvm)
    }
    return null
  }

  laskeLahetyksienKokonaismaara(toisto: LaskuLahetyksenToistoasetukset): number | null {
    const paattyminen = toisto.paattyminen

    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.LAHETYSMAARA) {
      return paattyminen.lahetysmaara
    }
    if (paattyminen.tyyppi === ToistonPaattymisTyyppi.PAATTYMISPVM) {
      let pvm = toisto.ensimmainenLahetyspvm
      let count = 1

      pvm = this.annaSeuraavaLahetysPvm(toisto, pvm)
      while (pvm <= paattyminen.paattymispvm) {
        count++
        pvm = this.annaSeuraavaLahetysPvm(toisto, pvm)
      }
      return count
    }
    return null
  }

  annaSallitutPaattymispaivamaarat(ensimmainenLahetyspvm: NumberDate, toistoTyyppi: LaskuToistoTyyppi, lahetyspaiva: KuukausittainenLahetysPaiva | VuosittainenLahetysPaiva | undefined): NumberDate[] {
    if (toistoTyyppi === LaskuToistoTyyppi.KERRAN) {
      return [ensimmainenLahetyspvm]
    }

    const osittainenAsetukset: LaskuLahetyksenToistoasetukset = {
      ensimmainenLahetyspvm,
      toistoTyyppi,
      lahetyspaiva,
      paattyminen: { tyyppi: ToistonPaattymisTyyppi.EI_PAATTYMISTA },
    }

    const maxLahetyksia = 100
    const maxPvm = this._dateService.dateToNumber(this._dateService.lisaaKuukausia(this._dateService.numberToDate(ensimmainenLahetyspvm), 120))

    const sallitutPvmt: NumberDate[] = [ensimmainenLahetyspvm]
    let pvm = ensimmainenLahetyspvm

    let iteraatiot = 0
    while (iteraatiot < maxLahetyksia) {
      pvm = this.annaSeuraavaLahetysPvm(osittainenAsetukset, pvm)
      if (!pvm || pvm > maxPvm) {
        break
      }
      sallitutPvmt.push(pvm)
      iteraatiot++
    }

    return sallitutPvmt
  }

  private _annaKuukaudenPaivat(pvm: Date): Date[] {
    const ensimmainen = this._dateService.kuukaudenEnsimmainen(pvm)
    const viimeinen = this._dateService.kuukaudenViimeinen(pvm)
    const paivat: Date[] = []

    let paiva = new Date(ensimmainen)
    while (paiva <= viimeinen) {
      paivat.push(new Date(paiva))
      paiva = this._dateService.lisaaPaivia(paiva, 1)
    }
    return paivat
  }

  numberDateValidoi(asetukset: LaskuLahetyksenToistoasetukset): LaskuLahetyksenToistoasetusVirhe[] {
    const virheet: LaskuLahetyksenToistoasetusVirhe[] = []

    if (!asetukset) {
      virheet.push({ kentta: 'asetukset', viesti: 'lasku.ajasta-dialog.validointi.asetukset-puuttuvat' })
      return virheet
    }

    if (asetukset.ensimmainenLahetyspvm) {
      const tanaan = this._dateService.currentNumberDate()
      if (asetukset.ensimmainenLahetyspvm < tanaan) {
        virheet.push({
          kentta: 'ensimmainenLahetyspvm',
          viesti: 'lasku.ajasta-dialog.validointi.ensimmainen-pvm-menneisyydessa',
        })
      }
    }

    if (asetukset.paattyminen?.tyyppi === ToistonPaattymisTyyppi.PAATTYMISPVM) {
      const paattymispvm = asetukset.paattyminen.paattymispvm

      if (paattymispvm && asetukset.ensimmainenLahetyspvm && paattymispvm <= asetukset.ensimmainenLahetyspvm) {
        virheet.push({
          kentta: 'paattymispvm',
          viesti: 'lasku.ajasta-dialog.validointi.paattymispvm-liian-aikainen',
        })
      }

      if (paattymispvm && asetukset.ensimmainenLahetyspvm && asetukset.toistoTyyppi !== LaskuToistoTyyppi.KERRAN) {
        const sallitut = this.annaSallitutPaattymispaivamaarat(asetukset.ensimmainenLahetyspvm, asetukset.toistoTyyppi, asetukset.lahetyspaiva)
        if (!sallitut.includes(paattymispvm)) {
          virheet.push({
            kentta: 'paattymispvm',
            viesti: 'lasku.ajasta-dialog.validointi.paattymispvm-ei-lahetyspvm',
          })
        }
      }
    }

    return virheet
  }
}
